import { useEffect } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";

import moment from "moment";
import { useFormik } from "formik";
import { toast } from "react-toastify";

import { Button, Grid2, Typography } from "@mui/material";
import { IconUserEdit } from "@tabler/icons-react";

import { Input } from "./Input";
import Loading from "./Loading";

import { studentUpdate } from "../services/students";
import { checkCep } from "../services/app";

import {
  formatCpfCnpj,
  formatPhone,
  phoneValidate,
  unMask,
} from "../helpers/format";

import { cpf } from "cpf-cnpj-validator";

const StudentDetailsList = ({ data, refetch: refetchUpdate }: any) => {
  const {
    data: dataCep,
    refetch,
    isFetching,
  } = useQuery({
    queryFn: async () => await checkCep(values.cep),
    queryKey: ["getCep"],
    enabled: false,
  });

  const { mutate, isPending } = useMutation({
    mutationFn: studentUpdate,
    onSuccess: () => {
      refetchUpdate();
      toast.success("Aluno atualizado");
    },
    onError: (e: { response: { data: { message: string } } }) => {
      toast.error(String(e?.response?.data?.message) || "Erro!");
    },
  });

  const { values, handleSubmit, handleChange, setFieldValue } = useFormik({
    initialValues: {
      id: "",
      fullName: "",
      nickName: "",
      birthDate: "",
      email: "",
      phone: "",
      document: "",
      state: "",
      city: "",
      neighborhood: "",
      street: "",
      number: "",
      complement: "",
      cep: "",
      documentUser: "",
      notes: "",
    },
    onSubmit: (values) => {
      if (!data?.responsable) {
        const requiredFields = [
          { field: values.fullName, label: "Nome completo" },
          { field: values.email, label: "E-mail" },
          { field: values.phone, label: "Telefone" },
          { field: values.document, label: "CPF" },
          { field: values.state, label: "Estado" },
          { field: values.city, label: "Cidade" },
          { field: values.neighborhood, label: "Bairro" },
          { field: values.street, label: "Rua" },
          { field: values.number, label: "Número" },
          { field: values.cep, label: "CEP" },
        ];

        for (const item of requiredFields) {
          if (!item.field || item.field.trim() === "") {
            toast.error(`Preencha o campo "${item.label}"`);
            return;
          }
        }
        if (!cpf.isValid(unMask(values.document))) {
          toast.error("CPF inválido");
          return;
        }

        if (!phoneValidate(formatPhone(values.phone))) {
          toast.error("Telefone inválido");
          return;
        }
      }

      if (data?.responsable) {
        if (!values.fullName) {
          toast.error(`Preencha o campo "Nome"`);
          return;
        }
      }
      mutate({
        ...values,
        phone: unMask(values.phone),
        document: unMask(values.document),
      });
    },
  });

  useEffect(() => {
    if (data) {
      setFieldValue("id", data?.id ?? "");
      setFieldValue("fullName", data?.fullName ?? "");
      setFieldValue("nickName", data?.nickName ?? "");
      setFieldValue("email", data?.email ?? "");
      setFieldValue("phone", data?.phone ?? "");
      setFieldValue("document", data?.document ?? "");
      setFieldValue(
        "birthDate",
        moment(data?.birthDate).format("YYYY-MM-DD").toString() ?? ""
      );
      setFieldValue("number", data?.address?.number ?? "");
      setFieldValue("complement", data?.address?.complement ?? "");
      setFieldValue("cep", data?.address?.cep ?? "");
      setFieldValue("city", data?.address?.city);
      setFieldValue("neighborhood", data?.address?.neighborhood ?? "");
      setFieldValue("state", data?.address?.state ?? "");
      setFieldValue("street", data?.address?.street ?? "");
      setFieldValue("notes", data?.notes ?? "");
    }
  }, [data]);

  useEffect(() => {
    if (dataCep) {
      setFieldValue("neighborhood", dataCep?.neighborhood);
      setFieldValue("state", dataCep?.state);
      setFieldValue("street", dataCep?.address);
      setFieldValue("city", dataCep?.city);
    }
  }, [dataCep]);

  return (
    <Grid2 container spacing={2}>
      <Loading loading={isFetching || isPending} />
      <Grid2 size={{ xs: 12, md: 4 }}>
        <Input
          id="fullName"
          name="fullName"
          label="Nome completo"
          placeholder="Digite o nome completo"
          type="text"
          fullWidth
          size="small"
          value={values.fullName}
          onChange={handleChange}
        />
      </Grid2>
      <Grid2 size={{ xs: 12, md: 4 }}>
        <Input
          id="nickName"
          name="nickName"
          label="Apelido (Opcional)"
          placeholder="Digite o apelido"
          type="text"
          fullWidth
          size="small"
          value={values.nickName}
          onChange={handleChange}
        />
      </Grid2>
      <Grid2 size={{ xs: 12, md: 4 }}>
        <Input
          id="birthDate"
          name="birthDate"
          label="Nascimento (Opcional)"
          type="date"
          fullWidth
          value={values.birthDate}
          onChange={handleChange}
          onFocus={(e: React.FocusEvent<HTMLInputElement>) =>
            e.target.showPicker()
          }
        />
      </Grid2>
      {data?.responsable && (
        <>
          <Grid2 size={12}>
            <Typography
              variant="body1"
              fontSize={16}
              fontWeight={600}
              color="secondary.contrastText"
            >
              DADOS DO RESPONSÁVEL
            </Typography>
          </Grid2>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <Input
              id="responsableName"
              name="responsableName"
              label="Nome"
              placeholder="Digite o nome completo"
              type="text"
              fullWidth
              size="small"
              disabled
              value={data?.responsable?.fullName}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <Input
              id="responsablePhone"
              name="responsablePhone"
              label="Telefone"
              placeholder="Digite telefone"
              type="text"
              fullWidth
              size="small"
              disabled
              value={formatPhone(data?.responsable?.phone)}
            />
          </Grid2>
        </>
      )}
      {!data?.responsable && (
        <>
          <Grid2 size={{ xs: 12, md: 4 }}>
            <Input
              id="document"
              name="document"
              label="CPF"
              placeholder="Digite o CPF"
              type="text"
              fullWidth
              size="small"
              value={formatCpfCnpj(values.document)}
              onChange={handleChange}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, md: 4 }}>
            <Input
              id="phone"
              name="phone"
              label="Telefone"
              placeholder="Digite o telefone"
              type="tel"
              fullWidth
              size="small"
              value={formatPhone(values.phone)}
              onChange={handleChange}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, md: 4 }}>
            <Input
              id="email"
              name="email"
              label="E-mail"
              placeholder="Digite o e-mail"
              type="text"
              fullWidth
              size="small"
              value={values.email}
              onChange={handleChange}
            />
          </Grid2>
          <Grid2 size={12}>
            <Typography
              variant="body1"
              fontSize={16}
              fontWeight={600}
              color="secondary.contrastText"
            >
              ENDEREÇO
            </Typography>
          </Grid2>
          <Grid2 size={12}>
            <Input
              id="cep"
              name="cep"
              label="CEP"
              type="text"
              fullWidth
              value={values.cep}
              onChange={handleChange}
              onBlur={(e) => {
                if (e.target.value.length) {
                  refetch();
                }
              }}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, md: 5 }}>
            <Input
              id="street"
              name="street"
              label="Rua"
              type="text"
              fullWidth
              value={values.street}
              onChange={handleChange}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, md: 4 }}>
            <Input
              id="neighborhood"
              name="neighborhood"
              label="Bairro"
              type="text"
              fullWidth
              value={values.neighborhood}
              onChange={handleChange}
            />
          </Grid2>{" "}
          <Grid2 size={{ xs: 12, md: 3 }}>
            <Input
              id="number"
              name="number"
              label="Número"
              type="text"
              fullWidth
              value={values.number}
              onChange={handleChange}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, md: 4 }}>
            <Input
              id="city"
              name="city"
              label="Cidade"
              type="text"
              fullWidth
              value={values.city}
              onChange={handleChange}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, md: 4 }}>
            <Input
              id="state"
              name="state"
              label="Estado"
              type="text"
              fullWidth
              value={values.state}
              onChange={handleChange}
            />
          </Grid2>
          <Grid2 size={4}>
            <Input
              id="complement"
              name="complement"
              label="Complemento (Opcional)"
              type="text"
              fullWidth
              value={values.complement}
              onChange={handleChange}
            />
          </Grid2>{" "}
          <Grid2 size={12}>
            <Typography
              variant="body1"
              fontSize={16}
              fontWeight={600}
              color="secondary.contrastText"
            >
              OBSERVAÇÂO
            </Typography>
          </Grid2>
          <Grid2 size={12}>
            <Input
              id="notes"
              name="notes"
              label="Observação (Opcional)"
              type="text"
              fullWidth
              value={values.notes}
              multiline
              rows={5}
              onChange={handleChange}
            />
          </Grid2>
        </>
      )}
      <Grid2 size={12} display={"flex"} justifyContent={"end"}>
        <Button
          variant="contained"
          size="medium"
          sx={{ gap: 0.5 }}
          onClick={() => handleSubmit()}
        >
          <IconUserEdit size={18} />
          <Typography variant="body2">Atualizar dados do aluno</Typography>
        </Button>
      </Grid2>
    </Grid2>
  );
};

export default StudentDetailsList;
